import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import createProvider from '@/shared/api/fulfilment/providers/createProvider';
import getProvider from '@/shared/api/fulfilment/providers/getProvider';
import getSupportedProviders from '@/shared/api/fulfilment/providers/getSupportedProviders';
import updateProvider from '@/shared/api/fulfilment/providers/updateProvider';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import DialogName from '@/shared/types/dialogName.enum';
import Provider from '@/shared/types/fulfilment/provider.type';
import ProviderAmazonFBA from '@/shared/types/fulfilment/providerAmazonFBA.type';
import SupportedProvider from '@/shared/types/fulfilment/supportedProvider.type';

import FulfilmentProviderDetails from '../components/AddFulfilmentProviderFormDialog/types/fulfilmentProviderDetails.type';

type OnUpdateDialogState = ReturnType<
  typeof useModalDialog<Provider, DialogName>
>['onUpdateDialogState'];

type OnUpdateNotificationBar = ReturnType<
  typeof useNotificationBar
>['onUpdateNotificationBar'];

type OnUpdateProgressDialog = ReturnType<
  typeof useProgressDialog
>['onUpdateProgressDialog'];

type Args = {
  onUpdateDialogState: OnUpdateDialogState;
  onUpdateNotificationBar: OnUpdateNotificationBar;
  onUpdateProgressDialog: OnUpdateProgressDialog;
};

const useFulfilmentProvidersOperations = ({
  onUpdateDialogState,
  onUpdateNotificationBar,
  onUpdateProgressDialog,
}: Args) => {
  const { t } = useTranslation(['fulfilmentProviders', 'common']);

  const redirectLogin = useRedirectLogin();

  const [refreshTableIndex, setRefreshTableIndex] = useState(0);

  const [supportedProviders, setSupportedProviders] = useState<
    SupportedProvider[]
  >([]);

  const handleAddNewProvider = async (
    values: FulfilmentProviderDetails & ProviderAmazonFBA,
  ) => {
    onUpdateProgressDialog({
      open: true,
      title: t('fulfilmentProviders:addingProvider'),
      progress: 0,
    });

    const response = await createProvider({
      body: values,
      onUnauthorized: redirectLogin,
    });

    if (response.success) {
      onUpdateDialogState(DialogName.UpdateProvider, { open: false });
      onUpdateNotificationBar({
        open: true,
        message: t('fulfilmentProviders:providerAddedSuccessfully'),
      });
      setRefreshTableIndex((prev) => prev + 1);
      onUpdateProgressDialog({ open: false });
    } else {
      onUpdateProgressDialog({ open: false });
    }
  };

  const handleUpdateProvider = async (
    providerId: string,
    data: Partial<Provider>,
  ) => {
    onUpdateProgressDialog({
      open: true,
      title: t('fulfilmentProviders:updatingProvider'),
      progress: 0,
    });

    const response = await updateProvider({
      providerId,
      data,
      onUnauthorized: redirectLogin,
    });

    if (response.success) {
      onUpdateDialogState(DialogName.UpdateProvider, { open: false });
      onUpdateNotificationBar({
        open: true,
        message: t('fulfilmentProviders:providerUpdatedSuccessfully'),
        severity: 'success',
      });
      setRefreshTableIndex((prev) => prev + 1);
      onUpdateProgressDialog({ open: false });
    } else {
      onUpdateProgressDialog({ open: false });
    }
  };

  const onOpenUpdateProviderDialog = async (provider: Provider) => {
    const getProviderResponse = await getProvider({
      providerId: provider.fulfilment_provider_id,
      onUnauthorized: () => redirectLogin(),
    });

    if (getProviderResponse.success) {
      onUpdateDialogState(DialogName.UpdateProvider, {
        open: true,
        data: getProviderResponse.data,
        errors: [],
      });
    }
  };

  useEffect(() => {
    const fetchSupportedProviders = async () => {
      const response = await getSupportedProviders();
      if (response.success) {
        setSupportedProviders(response.data);
      }
    };

    void fetchSupportedProviders();
  }, []);

  return {
    supportedProviders,
    refreshTableIndex,
    handleAddNewProvider,
    handleUpdateProvider,
    onOpenUpdateProviderDialog,
  };
};

export default useFulfilmentProvidersOperations;
